import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { useScrollCues } from '../hooks/useScrollCues';
import { guestTagChipStyle, tagLabel } from '../lib/guestTags';
import {
  arrivalsByTimeslot,
  formatSlotClock,
  groupSizeHistogram,
  overdueReservations,
  requestKindLabel,
  stripNoteHtml,
} from '../lib/liveBriefing';
import type {
  GuestTag,
  KitchenBriefing,
  KitchenBriefingOutlet,
  KitchenBriefingRequest,
  ServiceTimelineOps,
  ServiceTimelineReservation,
} from '../types';
import { Icon } from './shared/Icon';

type BriefingPartyRef = {
  reservationId?: string | null;
  startMs: number;
  peopleCount: number;
  tables?: string | null;
};

function findReservation(
  reservations: ServiceTimelineReservation[],
  ref: BriefingPartyRef,
): ServiceTimelineReservation | null {
  const rid = ref.reservationId?.trim();
  if (rid) {
    const byId = reservations.find(r => r.id === rid);
    if (byId) return byId;
  }
  // Fallback when older payloads lack reservationId.
  const table = ref.tables?.trim() || null;
  return (
    reservations.find(r => {
      if (r.startMs !== ref.startMs || r.peopleCount !== ref.peopleCount) {
        return false;
      }
      if (!table) return true;
      const names = r.tableNames ?? [];
      return names.some(n => n === table) || names.join(', ') === table;
    }) ?? null
  );
}

function pickKitchen(
  briefing: KitchenBriefing | null | undefined,
  outletId: string | null,
): KitchenBriefingOutlet | null {
  if (!briefing?.outlets?.length) return null;
  if (outletId) {
    return briefing.outlets.find(o => o.outletId === outletId) ?? null;
  }
  if (briefing.outlets.length === 1) return briefing.outlets[0] ?? null;
  // Multi-outlet without a pin: merge lightly; VIP/request lists stay empty
  // so notes never float without a restaurant context.
  const merged: KitchenBriefingOutlet = {
    outletId: '*',
    vipParties: [],
    tagCounts: [],
    foodNotes: [],
    seatingNotes: [],
    requests: [],
  };
  const tagMap = new Map<string, GuestTag & { count: number }>();
  const foodMap = new Map<string, number>();
  const seatMap = new Map<string, number>();
  for (const o of briefing.outlets) {
    for (const t of o.tagCounts) {
      const cur = tagMap.get(t.name) ?? {
        name: t.name,
        emoji: t.emoji,
        color: t.color,
        count: 0,
      };
      cur.count += t.count;
      if (!cur.emoji && t.emoji) cur.emoji = t.emoji;
      if (!cur.color && t.color) cur.color = t.color;
      tagMap.set(t.name, cur);
    }
    for (const n of o.foodNotes) {
      foodMap.set(n.text, (foodMap.get(n.text) ?? 0) + n.count);
    }
    for (const n of o.seatingNotes) {
      seatMap.set(n.text, (seatMap.get(n.text) ?? 0) + n.count);
    }
  }
  merged.tagCounts = [...tagMap.values()].sort((a, b) => b.count - a.count);
  merged.foodNotes = [...foodMap.entries()]
    .map(([text, count]) => ({ text, count }))
    .sort((a, b) => b.count - a.count);
  merged.seatingNotes = [...seatMap.entries()]
    .map(([text, count]) => ({ text, count }))
    .sort((a, b) => b.count - a.count);
  return merged;
}

function formatTablesLabel(tables: string): string {
  const t = tables.trim();
  if (!t) return '';
  // Bare numbers from aleno are table numbers — say so.
  if (/^\d+([,\s]+\d+)*$/.test(t)) {
    const parts = t.split(/[,\s]+/).filter(Boolean);
    return parts.length === 1
      ? i18n.t('live:briefing.tableN', { n: parts[0] })
      : i18n.t('live:briefing.tablesList', { list: parts.join(', ') });
  }
  if (/^tables?\b/i.test(t)) return t;
  return i18n.t('live:briefing.tableN', { n: t });
}

function PartyMeta({
  startMs,
  peopleCount,
  tables,
}: {
  startMs: number;
  peopleCount: number;
  tables?: string | null;
}) {
  const tableLabel = tables ? formatTablesLabel(tables) : '';
  return (
    <div className="live-brief-party-meta">
      <span className="live-brief-time">{formatSlotClock(startMs)}</span>
      <span className="live-brief-dot" aria-hidden />
      <span>
        {i18n.t('live:briefing.nGuests', { count: peopleCount })}
      </span>
      {tableLabel ? (
        <>
          <span className="live-brief-dot" aria-hidden />
          <span className="live-brief-tables">{tableLabel}</span>
        </>
      ) : null}
    </div>
  );
}

function RequestCard({
  item,
  onOpen,
}: {
  item: KitchenBriefingRequest;
  onOpen?: () => void;
}) {
  const text = stripNoteHtml(item.text);
  if (!text) return null;
  const clickable = !!onOpen;
  const className = `live-brief-req kind-${item.kind}${clickable ? ' is-clickable' : ''}`;
  if (clickable) {
    return (
      <button
        type="button"
        className={className}
        onClick={onOpen}
        aria-label={i18n.t('live:briefing.openNote', { text: text.slice(0, 80) })}
      >
        <div className="live-brief-req-top">
          <PartyMeta
            startMs={item.startMs}
            peopleCount={item.peopleCount}
            tables={item.tables}
          />
          <span className="live-brief-kind">{requestKindLabel(item.kind)}</span>
        </div>
        <p className="live-brief-req-text">{text}</p>
      </button>
    );
  }
  return (
    <article className={className}>
      <div className="live-brief-req-top">
        <PartyMeta
          startMs={item.startMs}
          peopleCount={item.peopleCount}
          tables={item.tables}
        />
        <span className="live-brief-kind">{requestKindLabel(item.kind)}</span>
      </div>
      <p className="live-brief-req-text">{text}</p>
    </article>
  );
}

function OverdueCard({
  reservation,
  onOpen,
}: {
  reservation: ServiceTimelineReservation;
  onOpen?: () => void;
}) {
  const tables = (reservation.tableNames ?? []).filter(Boolean).join(', ');
  const clickable = !!onOpen;
  const className = `live-brief-req kind-overdue${clickable ? ' is-clickable' : ''}`;
  const body = (
    <>
      <div className="live-brief-req-top">
        <PartyMeta
          startMs={reservation.startMs}
          peopleCount={reservation.peopleCount}
          tables={tables || null}
        />
        <span className="live-brief-kind">{i18n.t('live:briefing.finished')}</span>
      </div>
      <p className="live-brief-req-text">{i18n.t('live:briefing.pastEnd')}</p>
    </>
  );
  if (clickable) {
    return (
      <button
        type="button"
        className={className}
        onClick={onOpen}
        aria-label={i18n.t('live:briefing.openPastEnd', {
          time: formatSlotClock(reservation.startMs),
        })}
      >
        {body}
      </button>
    );
  }
  return <article className={className}>{body}</article>;
}

export function LiveBriefingPanel({
  timeline,
  kitchenBriefing,
  outletId,
  onSelectReservation,
}: {
  timeline: ServiceTimelineOps | null;
  kitchenBriefing?: KitchenBriefing | null;
  outletId: string | null;
  onSelectReservation?: (r: ServiceTimelineReservation) => void;
}) {
  const { t } = useTranslation('live');
  const reservations = timeline?.reservations ?? [];
  const groups = useMemo(
    () => groupSizeHistogram(reservations),
    [reservations],
  );
  const arrivals = useMemo(
    () => arrivalsByTimeslot(reservations),
    [reservations],
  );
  const {
    scrollRef: arrivalsScrollRef,
    canScrollUp: arrivalsCanScrollUp,
    canScrollDown: arrivalsCanScrollDown,
    scrollByPage: arrivalsScrollByPage,
  } = useScrollCues(arrivals.length);
  const kitchen = useMemo(
    () => pickKitchen(kitchenBriefing, outletId),
    [kitchenBriefing, outletId],
  );
  const overdue = useMemo(
    () => overdueReservations(reservations),
    [reservations],
  );

  const maxGroupGuests = groups[0]?.guests ?? 1;
  const maxArrivalGuests = useMemo(
    () => Math.max(1, ...arrivals.map(a => a.guests)),
    [arrivals],
  );
  const peakArrival = useMemo(() => {
    if (!arrivals.length) return null;
    return arrivals.reduce((best, slot) =>
      slot.guests > best.guests ? slot : best,
    );
  }, [arrivals]);

  const totalGuests = useMemo(
    () => groups.reduce((sum, g) => sum + g.guests, 0),
    [groups],
  );
  const totalBookings = useMemo(
    () => groups.reduce((sum, g) => sum + g.bookings, 0),
    [groups],
  );

  const hasMix = groups.length > 0 || arrivals.length > 0;
  const tagsUnavailable = kitchenBriefing?.tagsAvailable === false;
  const hasKitchen =
    !!kitchen &&
    (kitchen.vipParties.length > 0 ||
      kitchen.tagCounts.length > 0 ||
      kitchen.foodNotes.length > 0 ||
      kitchen.seatingNotes.length > 0 ||
      kitchen.requests.length > 0 ||
      tagsUnavailable);
  const vipCount = kitchen?.vipParties.length ?? 0;
  const requestCount = kitchen?.requests.length ?? 0;
  const hasAttention = overdue.length > 0 || vipCount > 0 || requestCount > 0;

  if (!hasMix && !hasKitchen && overdue.length === 0) return null;

  return (
    <section className="live-brief sec" data-ftue="live-briefing">
      <div className="sec-head">
        <h2>{t('briefing.title')}</h2>
        <span className="sh-meta">
          {totalBookings > 0
            ? `${t('briefing.nBookings', { count: totalBookings })} · ${t('briefing.nGuests', { count: totalGuests })}`
            : t('briefing.sub')}
          {peakArrival
            ? ` · ${t('briefing.peak', {
                time: formatSlotClock(peakArrival.timeMs),
                guests: peakArrival.guests,
              })}`
            : ''}
        </span>
      </div>

      <div className="live-brief-card">
        {hasMix && (
          <div className="live-brief-top">
            <div className="live-brief-panel">
              <div className="live-brief-lab">{t('briefing.groupSizes')}</div>
              {groups.length === 0 ? (
                <p className="live-brief-empty">{t('briefing.noBookings')}</p>
              ) : (
                <ul className="live-brief-bars">
                  {groups.map(g => (
                    <li key={g.people}>
                      <div className="live-brief-bar-label">
                        <strong>
                          {g.bookings}×{g.people}
                        </strong>
                        <span>
                          {t('briefing.nGuests', { count: g.guests })}
                        </span>
                      </div>
                      <div
                        className="live-brief-bar-track"
                        aria-hidden
                      >
                        <div
                          className="live-brief-bar-fill size"
                          style={{
                            width: `${Math.max(8, (g.guests / maxGroupGuests) * 100)}%`,
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="live-brief-panel arrivals-panel">
              <div className="live-brief-lab">{t('briefing.arrivals')}</div>
              {arrivals.length === 0 ? (
                <p className="live-brief-empty">{t('briefing.noArrivals')}</p>
              ) : (
                <div className="live-brief-arrivals-shell">
                  {arrivalsCanScrollUp && (
                    <button
                      type="button"
                      className="ops-scroll-cue up"
                      aria-label={t('briefing.scrollUp')}
                      onClick={() => arrivalsScrollByPage(-1)}
                    >
                      <Icon name="arrowUp" size={14} stroke={2.4} />
                    </button>
                  )}
                  {arrivalsCanScrollDown && (
                    <button
                      type="button"
                      className="ops-scroll-cue down"
                      aria-label={t('briefing.scrollDown')}
                      onClick={() => arrivalsScrollByPage(1)}
                    >
                      <Icon name="arrowDown" size={14} stroke={2.4} />
                    </button>
                  )}
                  <div
                    className="live-brief-arrivals-scroll"
                    ref={arrivalsScrollRef}
                  >
                    <ul className="live-brief-bars arrivals">
                      {arrivals.map(slot => {
                        const isPeak = peakArrival?.timeMs === slot.timeMs;
                        return (
                          <li
                            key={slot.timeMs}
                            className={isPeak ? 'peak' : undefined}
                          >
                            <span className="live-brief-time">
                              {formatSlotClock(slot.timeMs)}
                            </span>
                            <div className="live-brief-bar-track" aria-hidden>
                              <div
                                className="live-brief-bar-fill arrival"
                                style={{
                                  width: `${Math.max(6, (slot.guests / maxArrivalGuests) * 100)}%`,
                                }}
                              />
                            </div>
                            <span className="live-brief-bar-val">
                              <strong>{slot.guests}</strong>
                              <span className="live-brief-muted">
                                {' '}
                                {t('briefing.nGuests', { count: slot.guests })} ·{' '}
                                {t('briefing.nBookings', { count: slot.bookings })}
                              </span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {hasAttention && (
          <div className="live-brief-attention">
            <div className="live-brief-attention-head">
              <div className="live-brief-lab">{t('briefing.needsAttention')}</div>
              <span className="live-brief-attention-meta">
                {[
                  vipCount
                    ? t('briefing.vipParty', { count: vipCount })
                    : null,
                  requestCount
                    ? t('briefing.bookingNote', { count: requestCount })
                    : null,
                  overdue.length
                    ? t('briefing.pastEndCount', { count: overdue.length })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>

            {kitchen && kitchen.vipParties.length > 0 && (
              <div className="live-brief-vip-row">
                {kitchen.vipParties.map((v, i) => {
                  const reservation = onSelectReservation
                    ? findReservation(reservations, v)
                    : null;
                  const open = reservation
                    ? () => onSelectReservation?.(reservation)
                    : undefined;
                  const className = `live-brief-vip-chip${open ? ' is-clickable' : ''}`;
                  if (open) {
                    return (
                      <button
                        key={`${v.reservationId ?? v.startMs}-${v.peopleCount}-${i}`}
                        type="button"
                        className={className}
                        onClick={open}
                        aria-label={t('briefing.openVip', {
                          time: formatSlotClock(v.startMs),
                        })}
                      >
                        <span className="live-brief-vip-mark">{t('briefing.vip')}</span>
                        <PartyMeta
                          startMs={v.startMs}
                          peopleCount={v.peopleCount}
                          tables={v.tables}
                        />
                      </button>
                    );
                  }
                  return (
                    <div
                      key={`${v.reservationId ?? v.startMs}-${v.peopleCount}-${i}`}
                      className={className}
                    >
                      <span className="live-brief-vip-mark">{t('briefing.vip')}</span>
                      <PartyMeta
                        startMs={v.startMs}
                        peopleCount={v.peopleCount}
                        tables={v.tables}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {kitchen && kitchen.requests.length > 0 && (
              <div className="live-brief-req-grid">
                {kitchen.requests.map((r, i) => {
                  const reservation = onSelectReservation
                    ? findReservation(reservations, r)
                    : null;
                  return (
                    <RequestCard
                      key={`${r.reservationId ?? r.startMs}-${r.kind}-${r.text}-${i}`}
                      item={r}
                      onOpen={
                        reservation
                          ? () => onSelectReservation?.(reservation)
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            )}

            {overdue.length > 0 && (
              <div className="live-brief-req-grid">
                {overdue.map(r => (
                  <OverdueCard
                    key={r.id}
                    reservation={r}
                    onOpen={
                      onSelectReservation
                        ? () => onSelectReservation(r)
                        : undefined
                    }
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {kitchen &&
          (kitchen.tagCounts.length > 0 ||
            kitchen.foodNotes.length > 0 ||
            kitchen.seatingNotes.length > 0 ||
            tagsUnavailable) && (
            <div className="live-brief-extras">
              {(kitchen.tagCounts.length > 0 || tagsUnavailable) && (
                <div className="live-brief-panel">
                  <div className="live-brief-lab">{t('briefing.guestTags')}</div>
                  {tagsUnavailable && kitchen.tagCounts.length === 0 ? (
                    <p className="live-brief-empty">
                      {t('briefing.tagsUnavailable')}
                    </p>
                  ) : (
                    <ul className="live-brief-chips">
                      {kitchen.tagCounts.map(t => (
                        <li
                          key={t.name}
                          className="guest-tag-chip"
                          style={guestTagChipStyle(t.color)}
                        >
                          <strong>{tagLabel(t)}</strong>
                          <span>×{t.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {kitchen.foodNotes.length > 0 && (
                <div className="live-brief-panel">
                  <div className="live-brief-lab">{t('briefing.allergies')}</div>
                  <ul className="live-brief-chips">
                    {kitchen.foodNotes.map(n => (
                      <li key={n.text} className="warn">
                        <strong>{stripNoteHtml(n.text)}</strong>
                        {n.count > 1 && <span>×{n.count}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {kitchen.seatingNotes.length > 0 && (
                <div className="live-brief-panel">
                  <div className="live-brief-lab">{t('briefing.seating')}</div>
                  <ul className="live-brief-chips">
                    {kitchen.seatingNotes.map(n => (
                      <li key={n.text}>
                        <strong>{stripNoteHtml(n.text)}</strong>
                        {n.count > 1 && <span>×{n.count}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
      </div>
    </section>
  );
}
